import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { SeverityBadge } from "@/components/security/SeverityBadge";
import { PriorityBadge } from "@/components/security/PriorityBadge";
import { cn, humanCategory } from "@/lib/util";
import { useT } from "@/lib/i18n";

const SEVS = ["critical", "high", "medium", "low", "info"];
const STATUSES = ["new", "open", "triaged", "in_progress", "mitigated", "fixed", "accepted", "false_positive"];
const SORTS = [
  { id: "priority", label: "Prioridad" },
  { id: "risk", label: "Riesgo" },
  { id: "recent", label: "Reciente" },
];

export default function Findings() {
  const { t } = useT();
  const [items, setItems] = useState([]);
  const [sev, setSev] = useState("");
  const [status, setStatus] = useState("");
  const [sort, setSort] = useState("priority");

  useEffect(() => {
    const params = new URLSearchParams();
    if (sev) params.set("severity", sev);
    if (status) params.set("status", status);
    params.set("sort", sort);
    api.get(`/findings?${params}`).then(r => setItems(r.data));
  }, [sev, status, sort]);

  return (
    <div className="space-y-6 animate-fade-in" data-testid="findings-page">
      <header>
        <div className="overline mb-2">{t("findings.overline")}</div>
        <h1 className="font-display text-4xl font-medium tracking-tight">{t("findings.title")}</h1>
        <p className="text-muted-foreground text-sm mt-1">
          Ordenados por Prioridad de Remediación (severidad × exploitación activa × exposición).
        </p>
      </header>

      <div className="flex flex-wrap gap-2">
        <FilterGroup label={t("findings.filter.sev")} allLabel={t("findings.filter.all")}
                     options={SEVS} value={sev} onChange={setSev} testid="filter-sev" />
        <FilterGroup label={t("findings.filter.status")} allLabel={t("findings.filter.all")}
                     options={STATUSES} value={status} onChange={setStatus} testid="filter-status" />
        <div className="flex items-center gap-1 border border-border p-1" data-testid="sort-group">
          <span className="font-mono text-[10px] tracking-widest uppercase text-muted-foreground px-2">Orden</span>
          {SORTS.map(s => (
            <button
              key={s.id}
              onClick={() => setSort(s.id)}
              data-testid={`sort-${s.id}`}
              className={cn(
                "font-mono text-[10px] tracking-widest uppercase px-2 py-1",
                sort === s.id ? "bg-primary/20 text-primary" : "text-muted-foreground hover:text-foreground"
              )}
            >
              {s.label}
            </button>
          ))}
        </div>
      </div>

      <div className="border border-border bg-card/60">
        <table className="w-full text-sm">
          <thead className="bg-background/50">
            <tr className="text-left overline border-b border-border">
              <th className="p-3">Prioridad</th>
              <th className="p-3">{t("findings.col.finding")}</th>
              <th className="p-3">{t("findings.col.category")}</th>
              <th className="p-3">{t("findings.col.sev")}</th>
              <th className="p-3">{t("findings.col.risk")}</th>
              <th className="p-3">{t("findings.col.cvss")}</th>
              <th className="p-3">{t("findings.col.epss")}</th>
              <th className="p-3">{t("findings.col.asset")}</th>
              <th className="p-3">{t("findings.col.status")}</th>
            </tr>
          </thead>
          <tbody>
            {items.map(f => (
              <tr key={f.id} data-row-hover className="border-b border-border/50" data-testid={`finding-row-${f.id}`}>
                <td className="p-3">
                  <PriorityBadge score={f.priority_score} label={f.priority_label} />
                  {(f.kev || f.exploit_available) && (
                    <div className="mt-1 text-[9px] font-mono uppercase tracking-widest text-sev-critical">
                      · exploit activo
                    </div>
                  )}
                </td>
                <td className="p-3">
                  <Link to={`/findings/${f.id}`} className="hover:text-primary">
                    <div>{f.title}</div>
                    {f.cve && (
                      <div className="font-mono text-[10px] text-muted-foreground mt-0.5">
                        {f.cve}{f.kev && <span className="ml-2 text-sev-critical">· KEV</span>}
                      </div>
                    )}
                  </Link>
                </td>
                <td className="p-3 font-mono text-[11px] text-muted-foreground">{humanCategory(f.category)}</td>
                <td className="p-3"><SeverityBadge sev={f.severity} /></td>
                <td className="p-3">
                  <span className={cn("font-mono text-sm font-bold",
                    f.risk_score >= 80 ? "text-sev-critical" : f.risk_score >= 60 ? "text-sev-high" :
                    f.risk_score >= 40 ? "text-sev-medium" : "text-sev-low"
                  )}>{f.risk_score}</span>
                </td>
                <td className="p-3 font-mono text-xs">{f.cvss ?? "—"}</td>
                <td className="p-3 font-mono text-xs">{f.epss ? `${(f.epss * 100).toFixed(0)}%` : "—"}</td>
                <td className="p-3 font-mono text-[11px] text-muted-foreground truncate max-w-[200px]">{f.asset_label}</td>
                <td className="p-3 font-mono text-[10px] tracking-widest uppercase text-muted-foreground">{f.status}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {items.length === 0 && <div className="p-8 text-center text-muted-foreground text-sm">{t("findings.empty")}</div>}
      </div>
    </div>
  );
}

function FilterGroup({ label, allLabel, options, value, onChange, testid }) {
  return (
    <div className="flex items-center gap-1 border border-border p-1">
      <span className="font-mono text-[10px] tracking-widest uppercase text-muted-foreground px-2">{label}</span>
      <button onClick={() => onChange("")}
              className={cn("font-mono text-[10px] tracking-widest uppercase px-2 py-1", !value ? "bg-primary/20 text-primary" : "text-muted-foreground hover:text-foreground")}
              data-testid={`${testid}-all`}>{allLabel}</button>
      {options.map(o => (
        <button key={o} onClick={() => onChange(o)}
                className={cn("font-mono text-[10px] tracking-widest uppercase px-2 py-1", value === o ? "bg-primary/20 text-primary" : "text-muted-foreground hover:text-foreground")}
                data-testid={`${testid}-${o}`}>{o}</button>
      ))}
    </div>
  );
}
